import { memo, useMemo } from 'react';

import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { SparklesIcon } from 'lucide-react';

import type {
  EnhancePanelAction,
  EnhanceSubcategory,
} from '@modules/_shared/components/image-gallery/enhance/enhance-image-types';
import { EnhanceActionCard } from '@modules/_shared/components/image-gallery/enhance/EnhanceActionCard';
import { useLocale } from '@shared/hooks/locale';

type Group = {
  id: string;
  title: string;
  emoji?: string;
  actions: EnhancePanelAction[];
};

type ActionPickerProps = {
  categories: Array<{ id: string; label: string; emoji: string }>;
  activeCategory: string;
  setActiveCategory: (id: string) => void;
  actions: EnhancePanelAction[];
  subcategories: EnhanceSubcategory[];
  onActionSelected: (action: EnhancePanelAction) => void;
  disabled?: boolean;
};

export const ActionPicker = memo(
  ({
    categories,
    activeCategory,
    setActiveCategory,
    actions,
    subcategories,
    onActionSelected,
    disabled = false,
  }: ActionPickerProps) => {
    const { t } = useLocale();

    const actionGroupsBySubcategory = useMemo(() => {
      if (activeCategory === 'all') {
        return null;
      }
      const inCategory = actions.filter(a => a.category === activeCategory);
      const subsForCategory = subcategories.filter(
        s => s.categoryId === activeCategory,
      );

      if (subsForCategory.length === 0) {
        return [
          {
            id: `${activeCategory}-all`,
            title: '',
            actions: inCategory,
          },
        ];
      }

      const bySub = inCategory.reduce((map, action) => {
        const key = action.subcategory ?? '';
        const list = map.get(key) ?? [];
        list.push(action);
        map.set(key, list);
        return map;
      }, new Map<string, EnhancePanelAction[]>());

      return subsForCategory.reduce<Group[]>((acc, sub) => {
        const subActions = bySub.get(sub.id);
        if (subActions?.length) {
          acc.push({
            id: sub.id,
            title: sub.label,
            emoji: sub.emoji,
            actions: subActions,
          });
        }
        return acc;
      }, []);
    }, [activeCategory, actions, subcategories]);

    return (
      <>
        <Box sx={{ flexShrink: 0, p: 2 }}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'center',
              mb: 0.5,
            }}
          >
            <SparklesIcon
              style={{ opacity: 0.7 }}
              size={20}
              color={'var(--mui-palette-primary-main)'}
            />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
              }}
            >
              {t('What would you like to do?')}
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              pl: 3.5,
            }}
          >
            {t('Pick an enhancement or write your own prompt')}
          </Typography>
        </Box>
        <Stack
          direction="row"
          sx={{
            flexWrap: 'wrap',
            gap: 0.75,
            flexShrink: 0,
            px: 2,
            pb: 2,
          }}
        >
          {categories.map(cat => (
            <Chip
              key={cat.id}
              label={
                <>
                  <span aria-hidden>{cat.emoji}</span> {cat.label}
                </>
              }
              variant={activeCategory === cat.id ? 'filled' : 'outlined'}
              color={activeCategory === cat.id ? 'primary' : 'default'}
              onClick={() => setActiveCategory(cat.id)}
            />
          ))}
        </Stack>
        <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
          {activeCategory === 'all' ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
                p: 2,
                pt: 0,
              }}
            >
              {actions.map(action => (
                <EnhanceActionCard
                  key={action.id}
                  action={action}
                  disabled={disabled}
                  onClick={() => onActionSelected(action)}
                />
              ))}
            </Box>
          ) : (
            actionGroupsBySubcategory?.map((group, index) => (
              <Box key={group.id}>
                {index > 0 && <Divider sx={{ mx: 2 }} />}
                <Box sx={{ px: 2, pt: index === 0 ? 0 : 2, pb: 2 }}>
                  {group.title && (
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{
                        alignItems: 'center',
                        mb: 1.5,
                      }}
                    >
                      {group.emoji && <span aria-hidden>{group.emoji}</span>}
                      <Typography
                        variant="subtitle2"
                        component="span"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                        }}
                      >
                        {group.title}
                      </Typography>
                    </Stack>
                  )}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 1,
                    }}
                  >
                    {group.actions.map(action => (
                      <EnhanceActionCard
                        key={action.id}
                        action={action}
                        disabled={disabled}
                        onClick={() => onActionSelected(action)}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </>
    );
  },
);
